import Overview from "../TabsCard/Overview";
import ServiceDescription from "../TabsCard/ServiceDescription";
import WorkExperience from "../TabsCard/WorkExperience";
import Qualification from "../TabsCard/Qualification";
import Documents from "../TabsCard/Documents";
import Deliverables from "../TabsCard/Deliverables";
import Shipments from "../TabsCard/Shipments";
import Schedule from "../TabsCard/Schedule";
import LocationMap from "../TabsCard/LocationMap";
import Contacts from "../TabsCard/Contacts";
import useAuth from "../../../hooks/useAuth";
import { useHasAcceptedProposal } from "../../../hooks/useHasAcceptedProposal";
import {
  CUSTOMER_EDITABLE_STATUSES,
  ModuleType,
  UserType,
  WorkOrderStatusType,
} from "../../../utils/enum";
import { workOrderStatus } from "../../../utils/variables";
import Tasks from "../TabsCard/Tasks";
import { useEffect, useState } from "react";
import { updateWorkOrderDetail } from "../../../store/workOrder/workOrderSlice";
import { useDispatch } from "react-redux";

const DetailsTab = ({ workOrderDetail }) => {
  const { auth, getPermissionForModule, authRole } = useAuth();
  const { hasUpdateAccess } = getPermissionForModule(ModuleType.WORK_ORDERS);
  // Customers hold CLIENT_WORK_ORDERS rather than WORK_ORDERS. They may edit what
  // they raised plus anything belonging to their own clients — a customer only ever
  // reaches work orders within that scope, and the API re-checks it on save.
  const { hasUpdateAccess: hasCustomerUpdateAccess } = getPermissionForModule(
    ModuleType.CLIENT_WORK_ORDERS,
  );
  const isCustomerEditor = authRole === UserType.CUSTOMER && hasCustomerUpdateAccess;
  // A customer may only edit while the work order is still theirs to shape —
  // Draft, Submitted, Accepted. Past that an engineer is assigned and changes
  // belong to the client, who keeps the wider window below.
  const isCustomerEditableStatus = CUSTOMER_EDITABLE_STATUSES.includes(workOrderDetail?.status);
  // Once the customer has accepted a client's proposal, the ticket is settled on
  // their side: only purchase orders (Pay tab) stay editable, nothing here does.
  const hasAcceptedProposal = useHasAcceptedProposal(
    workOrderDetail?.id,
    authRole === UserType.CUSTOMER,
  );
  const canEditDetails = isCustomerEditor
    ? isCustomerEditableStatus && !hasAcceptedProposal
    : hasUpdateAccess && !["PAID", "CANCELLED"].includes(workOrderDetail?.status);

  const showWorkExperience =
    authRole !== UserType.CUSTOMER ||
    ![WorkOrderStatusType.DRAFT, WorkOrderStatusType.PENDING_APPROVAL].includes(
      workOrderDetail?.status,
    );
  const [isEdit, setIsEdit] = useState(false);
  const dispatch = useDispatch();
  const isAssignedEngineer = auth.user.id === workOrderDetail?.engineer?.id;
  const allowedStatuses = workOrderStatus.slice(
    workOrderStatus.indexOf(WorkOrderStatusType.INPROGRESS),
  );
  const allowedContactStatuses = workOrderStatus.slice(
    workOrderStatus.indexOf(WorkOrderStatusType.ASSIGNED),
  );
  useEffect(() => {
    if (isEdit) {
      const formattedWorkOrder = formatWorkOrderDetails(workOrderDetail);
      dispatch(updateWorkOrderDetail(formattedWorkOrder));
    } else {
      // dispatch(resetWorkOrderForm());
    }
  }, [workOrderDetail]);

  const formatWorkOrderDetails = (workOrder) => {
    const formattedQualificationSubTypes = workOrder?.qualificationSubTypes?.reduce((acc, curr) => {
      const qualificationTypeId = curr.qualificationType.id;
      const existingQualification = acc.find(
        (item) => item.qualificationTypeId === qualificationTypeId,
      );

      if (existingQualification) {
        existingQualification.qualificationSubTypes.push(curr.id);
      } else {
        acc.push({
          id: Date.now(),
          qualificationTypeId: qualificationTypeId,
          qualificationSubTypes: [curr.id],
        });
      }

      return acc;
    }, []);

    return {
      summary: {
        overview: {
          title: workOrder?.title,
          isExportDisable: workOrder?.isExportDisable,
          clientId: workOrder?.client?.id,
          projectId: workOrder?.project?.id,
          teamId: workOrder?.team?.id,
          isExportDisable: workOrder?.isExportDisable,
          isCounterOffersDisable: workOrder?.isCounterOffersDisable,
          isGpsRequire: workOrder?.isGpsRequire,
          incidentId: workOrder?.incidentId,
        },
        serviceDescription: {
          description: workOrder?.description,
          confidentialInformation: workOrder?.confidentialInformation,
        },
        workExperience: {
          categoryId: workOrder?.category?.id,
          vendorId: workOrder?.category?.vendor?.id,
          workOrderServices: workOrder?.category?.vendor?.services.map((service) => service?.id),
        },
        deliverables: {
          files: workOrder?.deliverables?.files || [],
        },
        customerFiles: {
          files: workOrder?.customerFiles || [],
          isCustomerFilesVisible: !!workOrder?.customerFiles?.length,
        },
      },

      scheduling: {
        qualifications: formattedQualificationSubTypes,
        location: {
          addressId: workOrder?.address?.id,
          type: "EXACT_LOCATION",
          displayName: workOrder?.address?.displayName,
          locationType: workOrder?.address?.locationType,
          country: workOrder?.address?.country,
          addressLine1: workOrder?.address?.addressLine1,
          addressLine2: workOrder?.address?.addressLine2,
          city: workOrder?.address?.city,
          state: workOrder?.address?.state,
          zipCode: workOrder?.address?.zipCode,
          longitude: workOrder?.address?.longitude,
          latitude: workOrder?.address?.latitude,
          saveForFuture: workOrder?.saveForFuture,
          isRemote: workOrder?.isRemote,
          timeZone: workOrder?.timeZone,
        },
        schedule: {
          scheduleType: workOrder?.scheduleType,
          orderStartDate: workOrder?.orderStartDate,
          orderStartTime: workOrder?.orderStartTime,
          orderEndDate: workOrder?.orderEndDate,
          orderEndTime: workOrder?.orderEndTime,
          timeZone: workOrder?.timeZone,
        },
      },
      payment: {
        pay: {
          paymentTerms: workOrder?.paymentTerms,
          paymentType: workOrder?.paymentType,
          hourlyRate: workOrder?.hourlyRate,
          minimumHours: workOrder?.minimumHours,
          fixedAmount: workOrder?.fixedAmount,
          halfDayRate: workOrder?.halfDayRate,
          fullDayRate: workOrder?.fullDayRate,
          fthRate: workOrder?.fthRate,
          additionalHourlyRate: workOrder?.additionalHourlyRate,
          nonBusinessHourRate: workOrder?.nonBusinessHourRate,
          customHours: workOrder?.customHours,
          customHoursRate: workOrder?.customHoursRate,
          currency: workOrder?.currency,
          purchaseOrderNumber: workOrder?.purchaseOrderNumber,
        },

        expenses: workOrder?.Expense,
      },
    };
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="col-span-2">
        <Overview workOrderDetail={workOrderDetail} hasUpdateAccess={canEditDetails} />
      </div>
      <div className="col-span-2">
        <ServiceDescription
          incidentId={workOrderDetail?.incidentId}
          description={workOrderDetail?.description}
          confidentialInformation={workOrderDetail?.confidentialInformation}
          hasUpdateAccess={canEditDetails}
        />
      </div>
      {showWorkExperience && (
        <div className="lg:col-span-1 col-span-2">
          <WorkExperience
            category={workOrderDetail?.category}
            vendor={workOrderDetail?.vendor}
            services={workOrderDetail?.services}
            authRole={authRole}
            hasUpdateAccess={canEditDetails}
          />
        </div>
      )}

      <div className={showWorkExperience ? "lg:col-span-1 col-span-2" : "col-span-2"}>
        <Qualification
          qualifications={workOrderDetail?.qualificationSubTypes}
          authRole={authRole}
          createdById={workOrderDetail?.createdBy?.id}
          authUserId={auth.user.id}
          workOrderDetails={workOrderDetail}
          setIsEdit={setIsEdit}
          isEdit={isEdit}
          hasUpdateAccess={canEditDetails}
          currentStatus={workOrderDetail?.status}
        />
      </div>
      <div className="lg:col-span-1 col-span-2">
        <Schedule
          currentStatus={workOrderDetail?.status}
          schedule={{
            timeZone: workOrderDetail?.timeZone,
            scheduleType: workOrderDetail?.scheduleType,
            orderStartDate: workOrderDetail?.orderStartDate,
            orderStartTime: workOrderDetail?.orderStartTime,
            orderEndDate: workOrderDetail?.orderEndDate,
            orderEndTime: workOrderDetail?.orderEndTime,
            confirmedWorkOrderAt: workOrderDetail?.tasks?.find(
              (task) => task.taskType === "START_TIME",
            )?.completedTask?.text,
            onTheWayWorkOrderAt: workOrderDetail?.tasks?.find(
              (task) => task.taskType === "ON_THE_WAY",
            )?.completedTask?.text,
            runningLateWorkOrderAt: workOrderDetail?.tasks?.find(
              (task) => task.taskType === "RUNNING_LATE",
            )?.completedTask?.text,
            runningLateWorkOrderReason: workOrderDetail?.tasks?.find(
              (task) => task.taskType === "RUNNING_LATE",
            )?.reason,
            checkInAt: workOrderDetail?.tasks?.find((task) => task.taskType === "CHECK_IN")
              ?.completedTask?.text,
            checkOutAt: workOrderDetail?.tasks?.find((task) => task.taskType === "CHECK_OUT")
              ?.completedTask?.text,
          }}
          minimumHours={workOrderDetail?.minimumHours}
          paymentType={workOrderDetail?.paymentType}
          id={workOrderDetail?.id}
          authRole={authRole}
          hasUpdateAccess={canEditDetails}
        />
      </div>
      <div className="lg:col-span-1 col-span-2">
        <LocationMap
          location={workOrderDetail?.address}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          authRole={authRole}
          createdById={workOrderDetail?.createdBy?.id}
          authUserId={auth.user.id}
          hasUpdateAccess={canEditDetails}
        />
      </div>
      {(authRole === UserType.CLIENT ||
        authRole === UserType.CUSTOMER ||
        (isAssignedEngineer &&
          allowedContactStatuses.includes(workOrderDetail?.status) &&
          workOrderDetail?.subStatus !== "NOT_CONFIRMED")) && (
        <div className="col-span-2">
          <Documents
            customerFiles={workOrderDetail?.customerFiles}
            authRole={authRole}
            hasUpdateAccess={canEditDetails}
          />
        </div>
      )}
      {(authRole === UserType.CLIENT ||
        authRole === UserType.CUSTOMER ||
        (isAssignedEngineer && allowedStatuses.includes(workOrderDetail?.status))) &&
        workOrderDetail?.deliverables && (
          <div className="col-span-2">
            <Deliverables
              isAssignedEngineer={isAssignedEngineer}
              workOrderDeliverables={workOrderDetail?.deliverables}
            />
          </div>
        )}
      <div className="col-span-2">
        <Tasks
          tasks={workOrderDetail?.formattedTasks}
          engineer={workOrderDetail?.engineer}
          hasUpdateAccess={canEditDetails}
        />
      </div>
      {(authRole === UserType.CLIENT ||
        authRole === UserType.CUSTOMER ||
        (isAssignedEngineer &&
          allowedContactStatuses.includes(workOrderDetail?.status) &&
          workOrderDetail?.subStatus !== "NOT_CONFIRMED")) && (
        <div className="col-span-2">
          <Contacts
            contacts={workOrderDetail?.contacts}
            // Show the real Work Order Manager, never createdBy, for a customer
            // (whose own name would appear) or a submitted ticket (which has no
            // manager until a client publishes it).
            workOrderManager={
              authRole === UserType.CUSTOMER ||
              workOrderDetail?.status === WorkOrderStatusType.PENDING_APPROVAL
                ? workOrderDetail?.workOrderManager
                : workOrderDetail?.workOrderManager || workOrderDetail?.createdBy
            }
            authRole={authRole}
            workOrderId={workOrderDetail?.id}
            hasUpdateAccess={canEditDetails}
          />
        </div>
      )}
      <div className="col-span-2">{/* <Shipments /> */}</div>
      {/* <ProviderAssessment /> */}
      {/* <CustomField /> */}
    </div>
  );
};

export default DetailsTab;
